import React, { useEffect } from "react";
import ReactGA from "react-ga";
import { makeStyles } from "@material-ui/styles";
import MediaQuery from "react-responsive";
import { Redirect, Link } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Typography, Container, Grid } from "@material-ui/core";
import { Switch, Route, useRouteMatch, useParams } from "react-router-dom";
import { Skeleton as Skelly } from '@material-ui/lab';

import {
  getCompanyMetadata,
  getCompanyRemoteData,
  getApprovedCompanyReviews,
} from "db";

import CompanyImg from "./CompanyImg";
import Overview from "./Subpages/Overview";
import Reviews from "./Subpages/Reviews/index.js";

import CustomAppBar from "Components/CustomAppBar";
import SubPageHeader from "./SubpageHeader";

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: "1500px",
    margin: "auto",
    transition: "all 0.4s ease",
    overflow: "visible",
  },
  content: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    fontSize: "14px",
  },
  leftBar: {
    height: "calc(100vh - 81px)",
    paddingTop: 16,
    width: "220px",
    borderRight: "1px solid lightgray",
    display: "inline-block",
  },
  mainPane: {
    padding: 24,
    height: "100%",
    display: "inline-block",
    paddingRight: 0,
    maxWidth: 900,
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      maxWidth: 100000,
    },
  },
  backToCompanies: {
    color: "inherit",
    cursor: "pointer",
    marginTop: "5px",
    "&:hover": {
      color: "gray",
    },
  },
  companyIconContainer: {
    position: "sticky",
    top: "10px",
    minWidth: "170px",
    textAlign: "center",
  },
  "@media (min-width: 960px)": {
    header: {
      height: "65px",
      "& div": {
        fontSize: "30px",
      },
      "& a": {
        fontSize: "30px",
      },
    },
  },
  "@media (min-width: 701px)": {
    mainPaneWrapper: {
      maxHeight: "800px",
    },
    mainPane: {
      width: "calc(100% - 220px)",
    }
  },
  "@media (max-width: 700px)": {
    header: {
      position: "fixed",
      top: 0,
      height: "50px",
      textAlign: "left",
      paddingLeft: "20px",
      fontWeight: "normal",
      display: "flex",
      alignItems: "center",
      color: "orange",
    },
    content: {
      flexDirection: "column",
      paddingTop: "17px",
    },
    companyIconContainer: {
      position: "relative",
    },
    mobileHeader: {
      display: "flex",
      flexDirection: "column-reverse",
    },
    aboutHeader: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    mainPane: {
      width: "100%",
      maxWidth: "100%",
    }
  },
}));

const Skeleton = (props) => 
  <Skelly animation="wave" {...props} />

export default function CompanyDetail(props) {
  const [goHome, setGoHome] = React.useState(false);
  const [currentSubpage, setCurrentSubpage] = React.useState(0);
  const [employees, setEmployees] = React.useState(0);
  const [icon, setIcon] = React.useState(null);
  const [hq, setHQ] = React.useState("");
  const [founded, setFounded] = React.useState("");
  const [about, setAbout] = React.useState("");
  const [website, setWebsite] = React.useState("");
  const [companyName, setCompanyName] = React.useState(useParams().companyName);
  const [remoteData, setRemoteData] = React.useState(null);
  const [reviews, setReviews] = React.useState([]);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [currentCompany, setCurrentCompany] = React.useState(null);

  const classes = useStyles();

  let coName = useParams().companyName;

  useEffect(() => {

    async function getCompanyData() {
      try {
        const metadata = await getCompanyMetadata(coName);
        const remoteData = await getCompanyRemoteData(coName);
        const reviews = await getApprovedCompanyReviews(coName);
  
        if (metadata) {
          const {
            companyData: { HQ, employees, website, founded, icon },
            trimmedAbout,
          } = metadata;

          setIcon(icon);
          setEmployees(employees);
          setHQ(HQ);
          setFounded(founded);
          setAbout(trimmedAbout);
          setWebsite(website);
        }
        else {
          setIcon(null);
          setEmployees(null);
          setHQ(null);
          setFounded(null);
          setAbout(null);
          setWebsite(null);
        }
        setCompanyName(coName);
        setRemoteData(remoteData);
        setReviews(reviews);
        setInitialLoad(false);
      } catch (error) {
        console.log('error: ', error);
      }
    }
    getCompanyData();
  }, [coName])

  const setSubpage = (val) => setCurrentSubpage(val);

  const handleGoHome = () => setGoHome(true);

  let companyImgSrc = icon ? icon.logo : null;

  if (notFound)
    return (
      <Redirect
        to={{ pathname: "/notfound", state: { from: window.location } }}
      />
    );
  if (goHome)
    return (
      <Redirect to={{ pathname: "/", state: { from: window.location } }} />
    );

  let remoteSince;
  let remoteUntil;
  let perm;
  let remotePolicy;
  let alwaysBeenRemote;
  let policyDuration;

  if (remoteData) {
    remoteSince = remoteData.remoteSince;
    remoteUntil = remoteData.remoteUntil;
    perm = remoteData.perm;
    remotePolicy = remoteData.remotePolicy;
    alwaysBeenRemote = remoteData.alwaysBeenRemote;
    policyDuration = remoteData.policyDuration;
  }

  let remoteSinceDate;

  if (remoteSince) {
    remoteSinceDate = remoteSince.toDate();
  }

  const bodyProps = {
    alwaysBeenRemote,
    currentSubpage,
    hq,
    employees,
    website,
    about,
    founded,
    remoteData,
    remoteSinceDate,
    remoteUntil,
    remotePolicy,
    policyDuration,
    perm,
    setSubpage,
    companyName: remoteData
      ? remoteData.companyName
      : "",
    companyImage: companyImgSrc,
    reviews,
    loading: initialLoad,
    initialLoad,
  };

  const MobileLayout = ({ loading }) => {

    let { path, url } = useRouteMatch();

    return (
      <div className={classes.container}>
        <div style={{ margin: "24px 0" }}>
          <div
            className={classes.mobileHeader}
            style={{ textAlign: "center" }}
          >
            <Link
              className={classes.backToCompanies}
              style={{ textDecoration: "none" }}
              to="/"
            >
              <ArrowBackIcon
                style={{
                  marginBottom: "-6px",
                  marginLeft: "-16px",
                  color: "gray",
                }}
              />
              <Typography
                variant="body2"
                display="inline"
                style={{ color: "gray" }}
              >
                COMPANIES
              </Typography>
            </Link>
          </div>
          <div className={classes.aboutHeader}>
            <CompanyImg
              imgSrc={companyImgSrc}
              style={{ marginTop: "25px" }}
              loading={loading}
            />
            { loading ? (
              <>
                <Skeleton variant="text">
                  <Typography
                    align="center"
                    variant="h6"
                    style={{ marginTop: 16, fontWeight: 300 }}
                  >
                    Placeholder
                  </Typography>
                </Skeleton>
              </>
              ) : (
                <>
                  <Typography
                    align="center"
                    variant="h6"
                    style={{ marginTop: 16, fontWeight: 300 }}
                  >
                    {remoteData.companyName}
                  </Typography>
                </>
              )
            }
          </div>
        </div>
        <div className={classes.content} style={{ marginTop: "20px" }}>
          <div className={classes.mainPane}>
            <Switch>
              <Route path={`${path}/reviews`}>
                <Grid>
                  <SubPageHeader
                    currentSubpage={1}
                    setSubpage={setSubpage}
                    url={url}
                  />
                  <Reviews {...bodyProps} />
                </Grid>
              </Route>
              <Route path={`${path}/`}>
                <Grid>
                  <SubPageHeader
                    currentSubpage={0}
                    setSubpage={setSubpage}
                    url={url}
                  />
                  <Overview {...bodyProps} />
                </Grid>
              </Route>
            </Switch>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <MediaQuery maxWidth={700}>
        <CustomAppBar
          backgroundColor="white"
          addBottomBorder={true}
          disableMargins
        />
      </MediaQuery>
      <MediaQuery minWidth={701}>
        <CustomAppBar backgroundColor="white" addBottomBorder={true} />
      </MediaQuery>
      <Container maxWidth="lg">
        <MediaQuery maxWidth={700}>
          <MobileLayout loading={initialLoad} />
        </MediaQuery>
        <MediaQuery minWidth={701}>
          <DesktopLayout 
            loading={initialLoad} 
            companyImgSrc={companyImgSrc}
            remoteData={remoteData}
            setSubpage={setSubpage}
            bodyProps={bodyProps}
          />
        </MediaQuery>
      </Container>
    </>
  );
}

function DesktopLayout({ 
  loading,
  companyImgSrc,
  remoteData,
  setSubpage,
  bodyProps,
}) {
  let { path, url } = useRouteMatch();
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <MediaQuery minWidth={700}>
          <div className={classes.leftBar}>
            <div className={classes.companyIconContainer}>
                <Link
                  className={classes.backToCompanies}
                  style={{
                    textDecoration: "none",
                    color: "gray",
                    marginLeft: -16,
                  }}
                  to="/"
                >

                  <ArrowBackIcon
                    style={{
                      marginBottom: "-6px",
                      marginRight: "4px",
                      color: "gray",
                    }}
                  />
                  COMPANIES
                </Link>
                <div style={{ textAlign: "center" }}>
                  <CompanyImg imgSrc={companyImgSrc} loading={loading} />
                </div>

              {!loading ? (
                <>
                  <Typography
                    align="center"
                    variant="h6"
                    style={{ padding: 8, marginTop: 16, fontWeight: 300 }}
                  > 
                    { remoteData.companyName }
                  </Typography>
                </>
              ) : (
                <>
                  <Skeleton style={{ margin: "auto" }}>
                    <Typography
                      align="center"
                      variant="h6"
                      style={{ padding: 8, marginTop: 16, fontWeight: 300 }}
                    > 
                      Amazon
                    </Typography>                        
                  </Skeleton>
                </>
              )}
            </div>
          </div>
        </MediaQuery>
        <div className={classes.mainPane}>
          <Switch>
            <Route path={`${path}/reviews`}>
              <Grid>
                <SubPageHeader
                  currentSubpage={1}
                  setSubpage={setSubpage}
                  url={url}
                />
                <Reviews {...bodyProps} />
              </Grid>
            </Route>
            <Route path={`${path}/`}>
              <Grid className={classes.mainPaneWrapper} style={{ maxHeight: "800px" }}>
                <SubPageHeader
                  currentSubpage={0}
                  setSubpage={setSubpage}
                  url={url}
                />
                <Overview {...bodyProps} />
              </Grid>
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
};
